/**
 * Google Sheets Integration Service
 * One-time OAuth authorization to access all Google Sheets
 */

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const DISCOVERY_DOCS = ['https://sheets.googleapis.com/$discovery/rest?version=v4'];
const SCOPES = 'https://www.googleapis.com/auth/spreadsheets.readonly https://www.googleapis.com/auth/drive.readonly';

export interface GoogleSheetsConnection {
  spreadsheetId: string;
  sheetName: string;
  range: string;
  refreshInterval?: number; // minutes
}

export interface GoogleSheetsData {
  values: any[][];
  spreadsheetId: string;
  sheetName: string;
  lastUpdated: string;
}

export interface SpreadsheetInfo {
  id: string;
  name: string;
  url: string;
  modifiedTime: string;
}

class GoogleSheetsService {
  private accessToken: string | null = null;
  private isInitialized: boolean = false;

  /**
   * Initialize Google API client
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    if (!GOOGLE_CLIENT_ID) {
      throw new Error('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in .env');
    }

    await this.loadGoogleAPI();

    await window.gapi.client.init({
      clientId: GOOGLE_CLIENT_ID,
      discoveryDocs: DISCOVERY_DOCS,
      scope: SCOPES
    });

    this.isInitialized = true;
  }

  /**
   * Load Google API script
   */
  private loadGoogleAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.gapi) {
        window.gapi.load('client:auth2', () => {
          resolve();
        });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        window.gapi.load('client:auth2', () => {
          resolve();
        });
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Authenticate with Google (One-time browser authorization)
   * Shows Google sign-in popup
   */
  async authenticate(): Promise<boolean> {
    try {
      await this.initialize();

      const authInstance = window.gapi.auth2.getAuthInstance();

      if (!authInstance.isSignedIn.get()) {
        // This will show the Google sign-in popup
        await authInstance.signIn();
      }

      this.accessToken = authInstance.currentUser.get().getAuthResponse().access_token;
      return true;
    } catch (error: any) {
      console.error('Google authentication failed:', error);
      if (error.error === 'popup_closed_by_user') {
        throw new Error('Sign-in popup was closed. Please try again.');
      }
      throw new Error('Failed to authenticate with Google. Please try again.');
    }
  }

  /**
   * Extract spreadsheet ID from Google Sheets URL
   */
  extractSpreadsheetId(url: string): string | null {
    // URL format: https://docs.google.com/spreadsheets/d/{SPREADSHEET_ID}/edit#gid=0
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null;
  }

  /**
   * List all Google Sheets from user's Drive
   * Returns list of spreadsheets with name, id, and url
   */
  async listAllSpreadsheets(maxResults: number = 100): Promise<SpreadsheetInfo[]> {
    try {
      if (!this.isSignedIn()) {
        const authenticated = await this.authenticate();
        if (!authenticated) {
          throw new Error('Failed to authenticate with Google');
        }
      }

      // Use Google Drive API to list spreadsheet files
      const response = await window.gapi.client.request({
        path: 'https://www.googleapis.com/drive/v3/files',
        method: 'GET',
        params: {
          q: "mimeType='application/vnd.google-apps.spreadsheet'",
          pageSize: maxResults,
          orderBy: 'modifiedTime desc',
          fields: 'files(id, name, modifiedTime, webViewLink)'
        }
      });

      const files = response.result.files || [];
      return files.map((file: any) => ({
        id: file.id,
        name: file.name,
        url: file.webViewLink || `https://docs.google.com/spreadsheets/d/${file.id}`,
        modifiedTime: file.modifiedTime
      }));
    } catch (error) {
      console.error('Error listing spreadsheets:', error);
      throw new Error('Failed to load spreadsheets. Please make sure you granted access to Google Drive.');
    }
  }

  /**
   * Fetch data from Google Sheets
   */
  async fetchSheetData(
    spreadsheetId: string,
    sheetName: string = 'Sheet1',
    range?: string
  ): Promise<GoogleSheetsData> {
    try {
      if (!this.accessToken) {
        const authenticated = await this.authenticate();
        if (!authenticated) {
          throw new Error('Failed to authenticate with Google');
        }
      }

      // Build range
      const fullRange = range ? `${sheetName}!${range}` : sheetName;

      const response = await window.gapi.client.sheets.spreadsheets.values.get({
        spreadsheetId,
        range: fullRange
      });

      return {
        values: response.result.values || [],
        spreadsheetId,
        sheetName,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching Google Sheets data:', error);
      throw error;
    }
  }


  /**
   * List available sheets in a spreadsheet
   */
  async listSheets(spreadsheetId: string): Promise<string[]> {
    try {
      if (!this.accessToken) {
        await this.authenticate();
      }

      const response = await window.gapi.client.sheets.spreadsheets.get({
        spreadsheetId
      });

      const sheets = response.result.sheets || [];
      return sheets.map((sheet: any) => sheet.properties.title);
    } catch (error) {
      console.error('Error listing sheets:', error);
      throw error;
    }
  }

  /**
   * Convert Google Sheets data to CSV format
   */
  convertToCSV(data: GoogleSheetsData): string {
    return data.values
      .map(row =>
        row.map((cell: any) => {
          const cellStr = String(cell || '');
          // Escape quotes and wrap in quotes if contains comma
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
      .join('\n');
  }

  /**
   * Test connection to a sheet
   */
  async testConnection(spreadsheetId: string): Promise<boolean> {
    try {
      const sheets = await this.listSheets(spreadsheetId);
      return sheets.length > 0;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if user is signed in
   */
  isSignedIn(): boolean {
    try {
      if (!this.isInitialized || !window.gapi || !window.gapi.auth2) return false;
      const authInstance = window.gapi.auth2.getAuthInstance();
      return authInstance && authInstance.isSignedIn.get();
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current user's email
   */
  getCurrentUserEmail(): string | null {
    try {
      if (!this.isSignedIn()) return null;
      const authInstance = window.gapi.auth2.getAuthInstance();
      const user = authInstance.currentUser.get();
      const profile = user.getBasicProfile();
      return profile ? profile.getEmail() : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Sign out
   */
  async signOut(): Promise<void> {
    if (window.gapi && window.gapi.auth2) {
      const authInstance = window.gapi.auth2.getAuthInstance();
      if (authInstance) {
        await authInstance.signOut();
      }
    }
    this.accessToken = null;
  }
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    gapi: any;
  }
}

export default new GoogleSheetsService();
